import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { VehicleToDoListParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'
import { AddVINDialog, ExplanationAccordion, SortableTableCell } from 'components'
import { enqueueSnackbar } from 'notistack'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { Add, Edit } from '@mui/icons-material'
import moment from 'moment'
import { ListBody } from 'components/list'

export default function NeedsOEMSiteCheckedForRecallsTodoListDetails() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [addVINDialogOpen, setAddVINDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({ isGettingVehicles: false, isCreatingVehicle: false })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    sortBy: 'createdDateTime',
    sortDirection: 'asc'
  })
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const onChangeSortByDirection = (sortBy: string) => {
    setParameters(prev => ({
      ...parameters,
      sortBy,
      sortDirection: prev.sortBy === sortBy && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getNeedsOEMSiteCheckedForRecallsToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getNeedsOEMSiteCheckedForRecallsToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
        console.log(response)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs OEM Site Checked For Recalls')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicles)
  }, [layout, loadingState.isGettingVehicles])

  useEffect(() => {
    getNeedsOEMSiteCheckedForRecallsToDoList(parameters)
  }, [getNeedsOEMSiteCheckedForRecallsToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={loadingState.isCreatingVehicle}
        onClose={() => {
          setAddVINDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isCreatingVehicle', true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setAddVINDialogOpen(false)
              getNeedsOEMSiteCheckedForRecallsToDoList(parameters)
            })
            .finally(() => {
              setLoadingState('isCreatingVehicle', false)
            })
        }}
        open={addVINDialogOpen}
      />
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='createdDateTime'>Created Date</MenuItem>
                    <MenuItem value='vehicleCheckedInDate'>Vehicle Checked-In Date</MenuItem>
                    <MenuItem value='exporter'>Exporter</MenuItem>
                    <MenuItem value='buyer'>Buyer</MenuItem>
                    <MenuItem value='vehicleType'>Vehicle Type</MenuItem>
                    <MenuItem value='vin'>VIN</MenuItem>
                    <MenuItem value='year'>Year</MenuItem>
                    <MenuItem value='make'>Make</MenuItem>
                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'exporter', 'buyer', 'vehicleType', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {filterField === 'createdDateTime' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                {filterField === 'vehicleCheckedInDate' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (filterField === 'createdDateTime' ? !filterValueFrom || !filterValueTo : !filterValue) ||
                      (filterField === 'vehicleCheckedInDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'createdDateTime') {
                        setParameters({
                          ...parameters,
                          createdDateFrom: filterValueFrom,
                          createdDateTo: filterValueTo
                        })
                      } else if (filterField === 'vehicleCheckedInDate') {
                        setParameters({
                          ...parameters,
                          vehicleCheckInDateFrom: filterValueFrom,
                          vehicleCheckInDateTo: filterValueTo
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>
                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        parameters.createdDateFrom ||
                          parameters.createdDateTo ||
                          parameters.vehicleCheckInDateFrom ||
                          parameters.vehicleCheckInDateTo ||
                          parameters.exporter ||
                          parameters.vehicleType ||
                          parameters.vin ||
                          parameters.year ||
                          parameters.make ||
                          parameters.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined,
                        vehicleCheckInDateFrom: undefined,
                        vehicleCheckInDateTo: undefined,
                        exporter: undefined,
                        buyer: undefined,
                        vehicleType: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setAddVINDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.createdDateFrom && parameters.createdDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created Date from "${moment(parameters.createdDateFrom).format('M/D/yyyy')}" to "${moment(
                      parameters.createdDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vehicleCheckInDateFrom && parameters.vehicleCheckInDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Checked-In Date from "${moment(parameters.vehicleCheckInDateFrom).format(
                      'M/D/yyyy'
                    )}" to "${moment(parameters.vehicleCheckInDateTo).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vehicleCheckInDateFrom: undefined,
                        vehicleCheckInDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.exporter && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter contains "${parameters.exporter}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporter: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.buyer && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Buyer contains "${parameters.buyer}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        buyer: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vehicleType && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Type contains "${parameters.vehicleType}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vehicleType: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains ${parameters.vin}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vin: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${parameters.year}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        year: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains ${parameters.make}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        make: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains ${parameters.model}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        model: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding='checkbox'>
                              <Checkbox
                                color='primary'
                                checked={selectedVehicles.length === totalCount}
                                indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== totalCount}
                                onChange={() => {
                                  if (selectedVehicles.length === totalCount) {
                                    setSelectedVehicles([])
                                  } else {
                                    setSelectedVehicles(vehicles ?? [])
                                  }
                                }}
                              />
                            </TableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='createdDateTime'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Created Date
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vehicleCheckedInDate'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Vehicle Checked-In Date
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='exporter'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Exporter
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='buyer'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Buyer
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vehicleType'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Vehicle Type
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vin'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              VIN
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='year'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Year
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='make'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Make
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='model'
                              sortBy={parameters.sortBy}
                              sortDirection={parameters.sortDirection}
                            >
                              Model
                            </SortableTableCell>
                            <TableCell padding='checkbox'></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vehicles?.map((vehicle, index) => (
                            <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell padding='checkbox' sx={{ fontSize: '7.99pt' }}>
                                <Checkbox
                                  color='primary'
                                  checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                                  onChange={() => {
                                    if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                      setSelectedVehicles(selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                                    } else {
                                      setSelectedVehicles([...selectedVehicles, vehicle])
                                    }
                                  }}
                                />
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4 ? 'bold' : 'inherit'
                                })}
                              >
                                {formatDate(vehicle.createdDateTime)}
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicle.vehicleCheckInDateTime)), 'days') >= 4
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicle.vehicleCheckInDateTime)), 'days') >= 4
                                      ? 'bold'
                                      : 'inherit'
                                })}
                              >
                                {formatDate(vehicle.vehicleCheckInDateTime)}
                              </TableCell>

                              <TableCell>{vehicle.exporterCompany?.name}</TableCell>

                              <TableCell>{vehicle.buyerCompany?.name}</TableCell>

                              <TableCell>{vehicle.vehicleType?.name}</TableCell>

                              <TableCell>{vehicle.vin}</TableCell>

                              <TableCell>{vehicle.year}</TableCell>

                              <TableCell>{vehicle.make}</TableCell>

                              <TableCell>{vehicle.model}</TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => {
                                    navigate('/vehicles/' + vehicle.vehicleGuid + '/recall')
                                  }}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                Vehicles appear in this to do list if the vehicle has been checked in and has no records under Vehicle Details -
                Recall. Vehicles only appear once in this to do list. || The "Needs OEM Site Checked for Recalls" To Do List
                column "Created Date" values will be in bold and red if current date is greater than Created Date + 4 days. ||
                The "Needs OEM Site Checked for Recalls" To Do List Column "Vehicle Checked-In Date" value will be in bold and
                red if current date is greater than Vehicle Checked-In Date + 4 days. Vehicle Checked-In Date is the date the
                first vehicle check-in image was associated with the vehicle. || Pressing "Edit" in the row of a vehicle
                navigates to that vehicle's "Vehicle Details - Recall" tab. || The Needs OEM Site Checked for Recalls "To Do
                List" is a list screen intended for users on a PC. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
